/**
 * React Context State Adapter
 * Simple React Context-based auth state management
 */
import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import { initialAuthState, authReducer, } from '../types';
import { AuthManager } from '../AuthManager';
import { PlanningCentreAuth } from '../PlanningCentreAuth';
/**
 * Auth context
 */
export const AuthContext = createContext(null);
/**
 * AuthProvider Component
 * Provides auth context with automatic token refresh
 */
export function AuthProvider({ children, httpClient, storage, refreshThresholdSeconds = 300, autoRefresh = true, onLogin, onLogout, onRefresh, onRefreshExhausted, onError, }) {
    const [state, dispatch] = useReducer(authReducer, initialAuthState);
    const stateRef = useRef(state);
    const managerRef = useRef(null);
    const planningCentreRef = useRef(null);
    const inititalizedRef = useRef(false);
    // Keep stateRef in sync with state
    useEffect(() => {
        stateRef.current = state;
    }, [state]);
    // Initialize manager in useEffect (only runs once, even in Strict Mode)
    useEffect(() => {
        if (inititalizedRef.current) {
            return;
        }
        inititalizedRef.current = true;
        const newManager = new AuthManager(httpClient, {
            storage,
            refreshThresholdSeconds,
            autoRefresh,
            onLogin,
            onLogout,
            onRefresh,
            onRefreshExhausted,
            onError,
        });
        // Connect state adapter - use stateRef.current to get fresh state
        const adapter = {
            getState: () => stateRef.current,
            setState: () => { }, // Not used with dispatch
            subscribe: () => () => { },
            dispatch: (action) => dispatch(action),
        };
        newManager.connectStateAdapter(adapter);
        managerRef.current = newManager;
        planningCentreRef.current = new PlanningCentreAuth(httpClient, newManager);
        // Initialize auth from storage
        newManager.initialize();
        return () => {
            // DON'T destroy manager in cleanup - it's needed for the app lifecycle
            // Only cleanup if component is truly unmounting (not Strict Mode)
        };
    }, []); // Empty deps - only run once
    // Login
    const login = useCallback(async (email, password, rememberMe = false) => {
        if (!managerRef.current) {
            throw new Error('AuthManager not initialized');
        }
        return managerRef.current.login({ email, password, rememberMe });
    }, []);
    // Logout
    const logout = useCallback(async () => {
        if (!managerRef.current) {
            throw new Error('AuthManager not initialized');
        }
        return managerRef.current.logout();
    }, []);
    // Refresh
    const refresh = useCallback(async () => {
        if (!managerRef.current) {
            throw new Error('AuthManager not initialized');
        }
        return managerRef.current.forceRefresh();
    }, []);
    // Role check
    const hasRole = useCallback((role) => {
        return state.user?.roles?.includes(role) ?? false;
    }, [state.user]);
    // Right check
    const hasRight = useCallback((right) => {
        return state.user?.rights?.includes(right) ?? false;
    }, [state.user]);
    // Any role check
    const hasAnyRole = useCallback((roles) => {
        return roles.some((role) => state.user?.roles?.includes(role) ?? false);
    }, [state.user]);
    // All roles check
    const hasAllRoles = useCallback((roles) => {
        return roles.every((role) => state.user?.roles?.includes(role) ?? false);
    }, [state.user]);
    const contextValue = {
        state,
        dispatch,
        manager: managerRef.current,
        planningCentre: planningCentreRef.current,
        login,
        logout,
        refresh,
        hasRole,
        hasRight,
        hasAnyRole,
        hasAllRoles,
    };
    // Don't render children until manager is initialized
    if (!managerRef.current) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement(AuthContext.Provider, { value: contextValue }, children));
}
/**
 * useAuth hook
 * Access auth context
 */
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
/**
 * useUser hook
 * Get current user
 */
export function useUser() {
    const { state } = useAuth();
    return state.user;
}
/**
 * useIsAuthenticated hook
 * Check if user is authenticated
 */
export function useIsAuthenticated() {
    const { state } = useAuth();
    return state.isAuthenticated;
}
/**
 * useAuthLoading hook
 * Check if auth is loading
 */
export function useAuthLoading() {
    const { state } = useAuth();
    return state.isLoading || state.isInitializing;
}
/**
 * useAuthError hook
 * Get auth error
 */
export function useAuthError() {
    const { state } = useAuth();
    return state.error;
}
/**
 * useRoles hook
 * Get user roles
 */
export function useRoles() {
    const { state } = useAuth();
    return state.user?.roles ?? [];
}
/**
 * useRights hook
 * Get user rights/permissions
 */
export function useRights() {
    const { state } = useAuth();
    return state.user?.rights ?? [];
}
/**
 * useHasRole hook
 * Check if user has specific role
 */
export function useHasRole(role) {
    const roles = useRoles();
    return roles.includes(role);
}
/**
 * useHasRight hook
 * Check if user has specific right
 */
export function useHasRight(right) {
    const rights = useRights();
    return rights.includes(right);
}
/**
 * Create React Context state adapter for AuthManager
 */
export function createContextAdapter(getState, dispatch) {
    const listeners = new Set();
    return {
        getState,
        setState: () => {
            // Not used with dispatch
        },
        subscribe: (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        dispatch: (action) => {
            dispatch(action);
            // Notify listeners
            listeners.forEach((l) => l(getState()));
        },
    };
}
//# sourceMappingURL=context.js.map